// src/components/Layout/CollapsibleAdminPanel.js
import React, { useState } from 'react';
import styles from './CollapsibleAdminPanel.module.css';
import { ItemData } from '../../data/ItemData';
import { MonsterData } from '../../data/monsterData';
import { skillData, achievementData } from '../../data/data';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const CollapsibleAdminPanel = ({ playerData, onPlayerDataChange, onAddMessage }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [selectedItemKey, setSelectedItemKey] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);
  const [monsterKillAmount, setMonsterKillAmount] = useState(10);
  const [selectedMonsterKey, setSelectedMonsterKey] = useState('');
  const [selectedSkillKey, setSelectedSkillKey] = useState('');
  const [skillLevelAmount, setSkillLevelAmount] = useState(1);
  const [skillXpAmount, setSkillXpAmount] = useState(100);
  const [selectedAchievementKey, setSelectedAchievementKey] = useState('');
  
  // New state for item search and filtering
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState('all');
  const [itemCategoryFilter, setItemCategoryFilter] = useState('all');

  const togglePanel = () => {
    setIsExpanded(!isExpanded);
  };

  // General admin functions
  const handleAddGold = (amount) => {
    onPlayerDataChange(prevData => ({
      ...prevData,
      gold: prevData.gold + amount
    }));
    onAddMessage(`Added ${amount} gold! Total: ${playerData.gold + amount}G`, 'success');
  };

  const handleRestoreStats = () => {
    onPlayerDataChange(prevData => ({
      ...prevData,
      health: { ...prevData.health, current: prevData.health.max },
      mana: { ...prevData.mana, current: prevData.mana.max },
      energy: { ...prevData.energy, current: prevData.energy.max }
    }));
    onAddMessage('All stats restored to maximum!', 'success');
  };

  const handleUnlockAllBuildings = () => {
    onPlayerDataChange(prevData => {
      const newBuildings = { ...prevData.buildings };
      Object.keys(newBuildings).forEach(buildingKey => {
        newBuildings[buildingKey] = { ...newBuildings[buildingKey], owned: true };
      });
      return { ...prevData, buildings: newBuildings };
    });
    onAddMessage('All buildings unlocked!', 'success');
  };

  const handleMaxUpgradeBuildings = () => {
    onPlayerDataChange(prevData => {
      const newBuildings = { ...prevData.buildings };
      Object.keys(newBuildings).forEach(buildingKey => {
        if (newBuildings[buildingKey].owned) {
          newBuildings[buildingKey] = { ...newBuildings[buildingKey], upgradeLevel: 4 };
        }
      });
      return { ...prevData, buildings: newBuildings };
    });
    onAddMessage('All owned buildings upgraded to maximum level!', 'success');
  };

  const handleAddItem = () => {
    if (!selectedItemKey) return;
    
    const baseItem = ItemData[selectedItemKey];
    if (!baseItem) return;

    onPlayerDataChange(prevData => {
      const newInventory = { ...prevData.inventory };
      
      if (baseItem.stackable) {
        newInventory[selectedItemKey] = (newInventory[selectedItemKey] || 0) + itemQuantity;
      } else {
        // For non-stackable items, create unique instances
        for (let i = 0; i < itemQuantity; i++) {
          const newItem = {
            id: `${selectedItemKey}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            key: selectedItemKey,
            name: baseItem.name,
            type: baseItem.type,
            toolType: baseItem.toolType || null,
            weaponType: baseItem.weaponType || null,
            slotType: baseItem.slotType || null,
            handedness: baseItem.handedness || null,
            damage: baseItem.damage || null,
            durability: baseItem.maxDurability || null,
            maxDurability: baseItem.maxDurability || null,
            weight: baseItem.weight || 0,
            goldValue: baseItem.goldValue || 0,
            img: baseItem.img || '',
            description: baseItem.description || '',
            effects: baseItem.effects || null,
            stackable: baseItem.stackable || false,
            materials: baseItem.materials || null,
            shopCategory: baseItem.shopCategory || null,
            shopPriceOverride: baseItem.shopPriceOverride || null,
            category: baseItem.category || null,
          };
          newInventory[newItem.id] = newItem;
        }
      }
      
      return { ...prevData, inventory: newInventory };
    });
    
    onAddMessage(`Added ${itemQuantity}x ${baseItem.name} to inventory!`, 'success');
  };

  const handleAddMonsterKills = () => {
    if (!selectedMonsterKey) return;
    
    onPlayerDataChange(prevData => {
      const newMonsterKills = { ...prevData.monsterKills };
      newMonsterKills[selectedMonsterKey] = (newMonsterKills[selectedMonsterKey] || 0) + monsterKillAmount;
      return { ...prevData, monsterKills: newMonsterKills };
    });
    
    onAddMessage(`Added ${monsterKillAmount} kills for ${MonsterData[selectedMonsterKey]?.name || selectedMonsterKey}!`, 'success');
  };

  // Helper function to calculate level from XP (matching the game's formula)
  const calculateLevelFromXP = (xp) => {
    if (xp <= 0) return 1;
    
    let level = 1;
    let totalXPNeeded = 0;
    
    while (totalXPNeeded < xp) {
      totalXPNeeded += (level * 100) + 100;
      if (totalXPNeeded <= xp) {
        level++;
      }
    }
    
    return level;
  };

  // Helper function to calculate XP for a given level
  const calculateXPForLevel = (level) => {
    if (level <= 1) return 0;
    
    let totalXP = 0;
    for (let i = 1; i < level; i++) {
      totalXP += (i * 100) + 100;
    }
    return totalXP;
  };

  const handleIncreaseSkillLevel = () => {
    if (!selectedSkillKey) return;
    
    onPlayerDataChange(prevData => {
      const newSkills = { ...prevData.skills };
      if (newSkills[selectedSkillKey]) {
        const currentLevel = newSkills[selectedSkillKey].level || 1;
        const newLevel = Math.min(currentLevel + skillLevelAmount, 100); // Cap at level 100
        const xpForNewLevel = calculateXPForLevel(newLevel);
        
        newSkills[selectedSkillKey] = {
          ...newSkills[selectedSkillKey],
          level: newLevel,
          xp: xpForNewLevel
        };
      }
      return { ...prevData, skills: newSkills };
    });
    
    onAddMessage(`Increased ${skillData[selectedSkillKey]?.name || selectedSkillKey} by ${skillLevelAmount} levels!`, 'success');
    setSelectedSkillKey('');
    setSkillLevelAmount(1);
  };

  const handleDecreaseSkillLevel = () => {
    if (!selectedSkillKey) return;
    
    onPlayerDataChange(prevData => {
      const newSkills = { ...prevData.skills };
      if (newSkills[selectedSkillKey]) {
        const currentLevel = newSkills[selectedSkillKey].level || 1;
        const newLevel = Math.max(currentLevel - skillLevelAmount, 1); // Minimum level 1
        const xpForNewLevel = calculateXPForLevel(newLevel);
        
        newSkills[selectedSkillKey] = {
          ...newSkills[selectedSkillKey],
          level: newLevel,
          xp: xpForNewLevel
        };
      }
      return { ...prevData, skills: newSkills };
    });
    
    onAddMessage(`Decreased ${skillData[selectedSkillKey]?.name || selectedSkillKey} by ${skillLevelAmount} levels!`, 'warning');
    setSelectedSkillKey('');
    setSkillLevelAmount(1);
  };

  const handleAddSkillXP = () => {
    if (!selectedSkillKey) return;
    
    onPlayerDataChange(prevData => {
      const newSkills = { ...prevData.skills };
      if (newSkills[selectedSkillKey]) {
        const currentXP = newSkills[selectedSkillKey].xp || 0;
        const newXP = currentXP + skillXpAmount;
        const newLevel = calculateLevelFromXP(newXP);
        
        newSkills[selectedSkillKey] = {
          ...newSkills[selectedSkillKey],
          level: newLevel,
          xp: newXP
        };
      }
      return { ...prevData, skills: newSkills };
    });
    
    onAddMessage(`Added ${skillXpAmount} XP to ${skillData[selectedSkillKey]?.name || selectedSkillKey}!`, 'success');
    setSelectedSkillKey('');
    setSkillXpAmount(100);
  };

  const handleMaxAllSkills = () => {
    onPlayerDataChange(prevData => {
      const newSkills = { ...prevData.skills };
      
      Object.keys(newSkills).forEach(skillKey => {
        if (newSkills[skillKey]) {
          const xpForLevel100 = calculateXPForLevel(100);
          newSkills[skillKey] = {
            ...newSkills[skillKey],
            level: 100,
            xp: xpForLevel100
          };
        }
      });
      
      return { ...prevData, skills: newSkills };
    });
    
    onAddMessage('All skills set to level 100!', 'success');
  };

  const handleResetAllSkills = () => {
    if (window.confirm('Are you sure you want to reset all skills to level 1?')) {
      onPlayerDataChange(prevData => {
        const newSkills = { ...prevData.skills };
        
        Object.keys(newSkills).forEach(skillKey => {
          if (newSkills[skillKey]) {
            newSkills[skillKey] = {
              ...newSkills[skillKey],
              level: 1,
              xp: 0
            };
          }
        });
        
        return { ...prevData, skills: newSkills };
      });
      
      onAddMessage('All skills reset to level 1!', 'warning');
    }
  };

  const handleCompleteAchievement = () => {
    if (!selectedAchievementKey) return;
    
    const achievement = achievementData[selectedAchievementKey];
    if (!achievement) return;
    
    onPlayerDataChange(prevData => {
      const completedAchievements = prevData.completedAchievements || [];
      
      if (completedAchievements.includes(selectedAchievementKey)) {
        onAddMessage(`Achievement "${achievement.title}" is already completed!`, 'warning');
        return prevData;
      }
      
      const newCompletedAchievements = [...completedAchievements, selectedAchievementKey];
      
      return {
        ...prevData,
        completedAchievements: newCompletedAchievements,
        gold: prevData.gold + (achievement.goldReward || 0)
      };
    });
    
    onAddMessage(`Achievement "${achievement.title}" completed! +${achievement.goldReward || 0} gold`, 'success');
  };

  const handleCompleteAllAchievements = () => {
    if (window.confirm('Are you sure you want to complete all achievements? This will add a lot of gold!')) {
      onPlayerDataChange(prevData => {
        const completedAchievements = prevData.completedAchievements || [];
        const newCompletedAchievements = [...achievementKeys];
        
        // Calculate total gold reward
        const totalGoldReward = achievementKeys.reduce((sum, key) => {
          return sum + (achievementData[key]?.goldReward || 0);
        }, 0);
        

        
        return {
          ...prevData,
          completedAchievements: newCompletedAchievements,
          gold: prevData.gold + totalGoldReward
        };
      });
      
      onAddMessage(`All achievements completed! +${achievementKeys.reduce((sum, key) => sum + (achievementData[key]?.goldReward || 0), 0)} gold`, 'success');
    }
  };

  const handleResetAllAchievements = () => {
    if (window.confirm('Are you sure you want to reset all achievements? This will remove all completed achievements!')) {
      onPlayerDataChange(prevData => ({
        ...prevData,
        completedAchievements: []
      }));
      
      onAddMessage('All achievements reset!', 'warning');
    }
  };

  const handleUnlockAllTiers = () => {
    onPlayerDataChange(prevData => ({
      ...prevData,
      unlockedTier: 10 // Set to a high tier
    }));
    onAddMessage('All tiers unlocked!', 'success');
  };

  const handleResetPlayerData = () => {
    if (window.confirm('Are you sure you want to reset all player data? This cannot be undone!')) {
      const defaultData = {
        name: playerData.name || "Hero",
        id: playerData.id || `char_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        health: { current: 100, max: 100 },
        mana: { current: 75, max: 75 },
        energy: { current: 100, max: 100 },
        unlockedTier: 1,
        gold: 250,
        currentLocation: 'home',
        tripInventory: {},
        currentCarryWeight: 0,
        unlockedSeeds: [], // Array of seed keys that have been unlocked for foraging
        equipment: {
          mainHand: null, offHand: null, helmet: null, chest: null, pants: null, 
          gloves: null, boots: null, belt: null, necklace: null, ring1: null, ring2: null, back: null,
        },
        inventory: { berries: 10 },
        skills: {
          combat: { level: 1, xp: 0 }, mining: { level: 1, xp: 0 }, woodcutting: { level: 1, xp: 0 },
          fishing: { level: 1, xp: 0 }, foraging: { level: 1, xp: 0 }, refining: { level: 1, xp: 0 },
          crafting: { level: 1, xp: 0 }, smithing: { level: 1, xp: 0 }, survival: { level: 1, xp: 0 },
          herblore: { level: 1, xp: 0 }
        },
        stats: {
          strength: 10, dexterity: 10, intelligence: 10, constitution: 10, luck: 0, defense: 0,
          health: { current: 100, max: 100 }, damageMin: 1, damageMax: 2,
        },
        carryCapacity: 100,
        buildings: {
          furnace: { owned: false, upgradeLevel: 0 }, forge: { owned: false, upgradeLevel: 0 },
          sawmill: { owned: false, upgradeLevel: 0 }, workbench: { owned: false, upgradeLevel: 0 },
          shopFront: { owned: false, upgradeLevel: 0 }, questBoard: { owned: false, upgradeLevel: 0 },
          anvil: { owned: false, upgradeLevel: 0 }, campfire: { owned: false, upgradeLevel: 0 },
          brewingStation: { owned: false, upgradeLevel: 0 }, tanningStation: { owned: false, upgradeLevel: 0 },
          runecraftingStation: { owned: false, upgradeLevel: 0 }, landPlot: { owned: false, upgradeLevel: 0 },
        dungeonEntrance: { owned: false, upgradeLevel: 0 },
          fletchingStation: { owned: false, upgradeLevel: 0 }, thievingDummy: { owned: false, upgradeLevel: 0 },
        },
        activeQuests: [],
        customerOrders: [],
        day: 1,
        monsterKills: {},
        playerStats: {
          totalMonstersKilled: 0, goldEarned: 0, goldSpent: 0, questsCompleted: 0, ordersCompleted: 0,
          itemsBought: 0, itemsSold: 0, buildingsPurchased: 0, buildingUpgrades: 0, daysRested: 0,
          totalPlayTimeMinutes: 0, sessionsPlayed: 0, maxSkillLevel: 1, minSkillLevel: 1,
          skillLevelUps: 0, totalDamageDealt: 0, maxDamageDealt: 0, combatVictories: 0, combatDefeats: 0,
          itemsCrafted: 0, itemsRefined: 0, fishCaught: 0, woodChopped: 0, stoneGathered: 0,
          totalOreMined: 0, itemsForaged: 0, mealsCookedCampfire: 0, potionsBrewed: 0
        },
        completedAchievements: [],
        dailyQuestOrderManagement: { questsPurchased: 0, ordersPurchased: 0, lastResetDay: 1 },
        dailyConsumablePurchases: { healingPotion: 0, energyPotion: 0, manaPotion: 0, bandage: 0, apple: 0, berries: 0, lastResetDay: 1 },
        timeOfDay: 'morning',
        gameTime: { hour: 8, minute: 0 },
        selectedDifficulty: 'medium',
        worldState: { isPaused: false, gameSpeed: 1 },
        settings: { autoSave: true, notifications: true },
        unlockedLocations: ['home', 'wilderness'],
        lastSaveTime: Date.now(),
        createdAt: Date.now(),
        version: '1.0.0'
      };
      
      onPlayerDataChange(() => defaultData);
      onAddMessage('Player data reset to default state!', 'warning');
    }
  };

  // Get all available items for spawning
  const availableItems = Object.entries(ItemData).filter(([key, item]) => 
    item.type !== 'building' && key !== 'placeholder'
  );

  // Filter and search items
  const filteredItems = availableItems.filter(([key, item]) => {
    // Search term filter
    const matchesSearch = itemSearchTerm === '' || 
      item.name.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
      key.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(itemSearchTerm.toLowerCase()));
    
    // Type filter
    const matchesType = itemTypeFilter === 'all' || item.type === itemTypeFilter;
    
    // Category filter
    const matchesCategory = itemCategoryFilter === 'all' || item.category === itemCategoryFilter;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  // Get unique item types for filter dropdown
  const itemTypes = [...new Set(availableItems.map(([key, item]) => item.type))].sort();
  
  // Get unique categories for filter dropdown
  const itemCategories = [...new Set(availableItems.map(([key, item]) => item.category).filter(Boolean))].sort();

  // Get monster keys for kill tracking from actual monster data
  const monsterKeys = Object.keys(MonsterData);
  
  // Get skill keys for skill level modification
  const skillKeys = Object.keys(skillData);
  
  // Get achievement keys for achievement completion
  const achievementKeys = Object.keys(achievementData);

  const renderGeneralControls = () => (
    <div className={styles.categoryContent}>
      <h4>Quick Actions</h4>
      <div className={styles.buttonGroup}>
        <button onClick={() => handleAddGold(1000)} className={styles.adminButton}>
          +1,000 Gold
        </button>
        <button onClick={() => handleAddGold(10000)} className={styles.adminButton}>
          +10,000 Gold
        </button>
        <button onClick={handleRestoreStats} className={styles.adminButton}>
          Restore All Stats
        </button>
      </div>
      
      <h4>Buildings</h4>
      <div className={styles.buttonGroup}>
        <button onClick={handleUnlockAllBuildings} className={styles.adminButton}>
          Unlock All Buildings
        </button>
        <button onClick={handleMaxUpgradeBuildings} className={styles.adminButton}>
          Max Upgrade All Buildings
        </button>
      </div>
      
      <h4>Progression</h4>
      <div className={styles.buttonGroup}>
        <button onClick={handleUnlockAllTiers} className={styles.adminButton}>
          Unlock All Tiers
        </button>
      </div>
      
      <h4>Danger Zone</h4>
      <div className={styles.buttonGroup}>
        <button onClick={handleResetPlayerData} className={styles.dangerButton}>
          Reset Player Data
        </button>
      </div>
    </div>
  );

  const renderItemSpawner = () => (
    <div className={styles.categoryContent}>
      <h4>Item Spawner</h4>
      
      {/* Search Input */}
      <div className={styles.formGroup}>
        <label>Search Items:</label>
        <input
          type="text"
          value={itemSearchTerm}
          onChange={(e) => setItemSearchTerm(e.target.value)}
          placeholder="Search by name, key, or description..."
          className={styles.searchInput}
        />
      </div>

      {/* Filter Controls */}
      <div className={styles.filterRow}>
        <div className={styles.filterGroup}>
          <label>Type:</label>
          <select 
            value={itemTypeFilter} 
            onChange={(e) => setItemTypeFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Types</option>
            {itemTypes.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label>Category:</label>
          <select 
            value={itemCategoryFilter} 
            onChange={(e) => setItemCategoryFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Categories</option>
            {itemCategories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Clear Filters Button */}
      <div className={styles.filterActions}>
        <button 
          onClick={() => {
            setItemSearchTerm('');
            setItemTypeFilter('all');
            setItemCategoryFilter('all');
          }}
          className={styles.clearFiltersButton}
        >
          Clear Filters
        </button>
        <span className={styles.resultsCount}>
          {filteredItems.length} items found
        </span>
      </div>
      
      {/* Item Selection */}
      <div className={styles.formGroup}>
        <label>Item:</label>
        <select 
          value={selectedItemKey} 
          onChange={(e) => setSelectedItemKey(e.target.value)}
          className={styles.selectInput}
        >
          <option value="">Select an item...</option>
          {filteredItems.map(([key, item]) => (
            <option key={key} value={key}>
              {item.name} ({item.type}) {item.category && `- ${item.category}`}
            </option>
          ))}
        </select>
      </div>
      
      <div className={styles.formGroup}>
        <label>Quantity:</label>
        <input 
          type="number" 
          value={itemQuantity} 
          onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
          min="1"
          max="999"
          className={styles.numberInput}
        />
      </div>
      
      <button 
        onClick={handleAddItem} 
        disabled={!selectedItemKey}
        className={styles.adminButton}
      >
        Add Item to Inventory
      </button>
      
      {selectedItemKey && (
        <div className={styles.itemPreview}>
          <img 
            src={getItemImageSrc(selectedItemKey)} 
            alt={ItemData[selectedItemKey]?.name}
            className={styles.itemPreviewImage}
          />
          <div className={styles.itemPreviewInfo}>
            <span className={styles.itemName}>{ItemData[selectedItemKey]?.name}</span>
            <span className={styles.itemType}>Type: {ItemData[selectedItemKey]?.type}</span>
            {ItemData[selectedItemKey]?.category && (
              <span className={styles.itemCategory}>Category: {ItemData[selectedItemKey]?.category}</span>
            )}
            <span className={styles.itemValue}>Value: {ItemData[selectedItemKey]?.goldValue || 0}G</span>
            <span className={styles.itemDescription}>{ItemData[selectedItemKey]?.description}</span>
          </div>
        </div>
      )}
    </div>
  );

  const renderMonsterKills = () => (
    <div className={styles.categoryContent}>
      <h4>Monster Kill Tracker</h4>
      <div className={styles.formGroup}>
        <label>Monster:</label>
        <select 
          value={selectedMonsterKey} 
          onChange={(e) => setSelectedMonsterKey(e.target.value)}
          className={styles.selectInput}
        >
          <option value="">Select a monster...</option>
          {monsterKeys.map(key => (
            <option key={key} value={key}>
              {MonsterData[key]?.name || key} (Current: {playerData.monsterKills?.[key] || 0})
            </option>
          ))}
        </select>
      </div>
      
      <div className={styles.formGroup}>
        <label>Amount to Add:</label>
        <input 
          type="number" 
          value={monsterKillAmount} 
          onChange={(e) => setMonsterKillAmount(parseInt(e.target.value) || 1)}
          min="1"
          max="999"
          className={styles.numberInput}
        />
      </div>
      
      <button 
        onClick={handleAddMonsterKills} 
        disabled={!selectedMonsterKey}
        className={styles.adminButton}
      >
        Add Monster Kills
      </button>
    </div>
  );

  const renderSkillsControls = () => (
    <div className={styles.categoryContent}>
      <h3>Skill Level Management</h3>
      
      <div className={styles.controlGroup}>
        <label>Select Skill:</label>
        <select 
          value={selectedSkillKey} 
          onChange={(e) => setSelectedSkillKey(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a skill...</option>
          {skillKeys.map(key => (
            <option key={key} value={key}>
              {skillData[key]?.name || key} (Level: {playerData.skills?.[key]?.level || 1}, XP: {playerData.skills?.[key]?.xp || 0})
            </option>
          ))}
        </select>
      </div>

      <div className={styles.controlGroup}>
        <label>Level Amount:</label>
        <input
          type="number"
          value={skillLevelAmount}
          onChange={(e) => setSkillLevelAmount(parseInt(e.target.value) || 1)}
          min="1"
          max="50"
          className={styles.input}
        />
      </div>

      <div className={styles.buttonGroup}>
        <button 
          onClick={handleIncreaseSkillLevel}
          disabled={!selectedSkillKey}
          className={styles.button}
        >
          Increase Level
        </button>
        <button 
          onClick={handleDecreaseSkillLevel}
          disabled={!selectedSkillKey}
          className={styles.button}
        >
          Decrease Level
        </button>
      </div>

      <div className={styles.controlGroup}>
        <label>XP Amount:</label>
        <input
          type="number"
          value={skillXpAmount}
          onChange={(e) => setSkillXpAmount(parseInt(e.target.value) || 100)}
          min="1"
          max="10000"
          className={styles.input}
        />
      </div>

      <div className={styles.buttonGroup}>
        <button 
          onClick={handleAddSkillXP}
          disabled={!selectedSkillKey}
          className={styles.button}
        >
          Add XP
        </button>
      </div>

      <div className={styles.divider}></div>
      
      <h3>Bulk Actions</h3>
      <div className={styles.buttonGroup}>
        <button 
          onClick={handleMaxAllSkills}
          className={styles.button}
        >
          Max All Skills (Level 100)
        </button>
        <button 
          onClick={handleResetAllSkills}
          className={styles.dangerButton}
        >
          Reset All Skills (Level 1)
        </button>
      </div>
    </div>
  );

  const renderAchievementsControls = () => (
    <div className={styles.categoryContent}>
      <h4>Achievement Completion</h4>
      <div className={styles.formGroup}>
        <label>Achievement:</label>
        <select 
          value={selectedAchievementKey} 
          onChange={(e) => setSelectedAchievementKey(e.target.value)}
          className={styles.selectInput}
        >
          <option value="">Select an achievement...</option>
          {achievementKeys.map(key => {
            const achievement = achievementData[key];
            const isCompleted = playerData.completedAchievements?.includes(key);
            return (
              <option key={key} value={key}>
                {achievement.title} - {achievement.category} {isCompleted ? '(✓ Completed)' : ''}
              </option>
            );
          })}
        </select>
      </div>
      
      <button 
        onClick={handleCompleteAchievement} 
        disabled={!selectedAchievementKey}
        className={styles.adminButton}
      >
        Complete Achievement
      </button>
      
      <div className={styles.divider}></div>
      
      <h4>Bulk Actions</h4>
      <div className={styles.buttonGroup}>
        <button 
          onClick={handleCompleteAllAchievements}
          className={styles.adminButton}
        >
          Complete All Achievements
        </button>
        <button 
          onClick={handleResetAllAchievements}
          className={styles.dangerButton}
        >
          Reset All Achievements
        </button>
      </div>
      
      {selectedAchievementKey && (
        <div className={styles.itemPreview}>
          <div>
            <strong>{achievementData[selectedAchievementKey]?.title}</strong>
            <p>{achievementData[selectedAchievementKey]?.description}</p>
            <p><strong>Reward:</strong> {achievementData[selectedAchievementKey]?.goldReward || 0} gold</p>
            <p><strong>Category:</strong> {achievementData[selectedAchievementKey]?.category}</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderCategoryContent = () => {
    switch (selectedCategory) {
      case 'general':
        return renderGeneralControls();
      case 'items':
        return renderItemSpawner();
      case 'monsters':
        return renderMonsterKills();
      case 'skills':
        return renderSkillsControls();
      case 'achievements':
        return renderAchievementsControls();
      default:
        return renderGeneralControls();
    }
  };

  return (
    <>
      {/* Toggle Tab Button */}
      <button 
        className={`${styles.toggleTab} ${isExpanded ? styles.expanded : ''}`}
        onClick={togglePanel}
        title={isExpanded ? "Close Admin Panel" : "Open Admin Panel"}
      >
        <span className={styles.tabIcon}>⚙️</span>
        <span className={styles.tabText}>Admin</span>
        <span className={`${styles.arrow} ${isExpanded ? styles.open : ''}`}>
          &#9654;
        </span>
      </button>

      {/* Collapsible Panel */}
      <div className={`${styles.adminPanel} ${isExpanded ? styles.expanded : ''}`}>
        <div className={styles.adminHeader}>
          <h3>Admin Panel</h3>
          <p className={styles.adminWarning}>⚠️ For testing purposes only</p>
        </div>
        
        <div className={styles.categoryTabs}>
          <button 
            onClick={() => setSelectedCategory('general')}
            className={`${styles.categoryTab} ${selectedCategory === 'general' ? styles.active : ''}`}
          >
            General
          </button>
          <button 
            onClick={() => setSelectedCategory('items')}
            className={`${styles.categoryTab} ${selectedCategory === 'items' ? styles.active : ''}`}
          >
            Items
          </button>
          <button 
            onClick={() => setSelectedCategory('monsters')}
            className={`${styles.categoryTab} ${selectedCategory === 'monsters' ? styles.active : ''}`}
          >
            Monsters
          </button>
          <button 
            onClick={() => setSelectedCategory('skills')}
            className={`${styles.categoryTab} ${selectedCategory === 'skills' ? styles.active : ''}`}
          >
            Skills
          </button>
          <button 
            onClick={() => setSelectedCategory('achievements')}
            className={`${styles.categoryTab} ${selectedCategory === 'achievements' ? styles.active : ''}`}
          >
            Achievements
          </button>
        </div>
        
        <div className={styles.adminContent}>
          {renderCategoryContent()}
        </div>
      </div>
    </>
  );
};

export default CollapsibleAdminPanel; 